import type { FoodCategory, StorageLocation, Unit } from "./types";

export interface CategoryRule {
  label: string;
  /** Where this category usually lives, used to preselect it in the quick-add sheet. */
  defaultLocation: StorageLocation;
  /** How this category is usually counted, used to preselect it in the quick-add sheet. */
  defaultUnit: Unit;
  /** Days of good quality from the purchase date, per location. */
  shelfLifeDays: Record<StorageLocation, number>;
}

/**
 * Everything the app knows about a food category, in one table — adding a
 * category means one entry here and one icon in Icon.tsx.
 *
 * Shelf lives are conservative "still fine if it looks and smells fine"
 * estimates, not food-safety guarantees; the user can override any item's date.
 * Freezer numbers describe quality, not safety — frozen food stays safe far
 * longer than it stays good. Combinations nobody should actually use (meat left
 * in the pantry) get one day, so the item surfaces immediately as expiring
 * instead of being dated into the past.
 *
 * Note "pantry" and "frozen" are *kinds of food* here — tins and dry goods, and
 * food bought already frozen — which is a different question from where an item
 * is being kept. A bag of frozen peas moved to the fridge to thaw is still the
 * "frozen" category, and its shelf life drops accordingly.
 */
export const CATEGORY_RULES: Record<FoodCategory, CategoryRule> = {
  produce: {
    label: "Produce",
    defaultLocation: "fridge",
    defaultUnit: "pcs",
    shelfLifeDays: { fridge: 7, freezer: 240, pantry: 4 },
  },
  meat: {
    label: "Meat",
    defaultLocation: "fridge",
    defaultUnit: "g",
    shelfLifeDays: { fridge: 3, freezer: 180, pantry: 1 },
  },
  dairy: {
    label: "Dairy",
    defaultLocation: "fridge",
    defaultUnit: "ml",
    shelfLifeDays: { fridge: 10, freezer: 90, pantry: 1 },
  },
  pantry: {
    label: "Pantry",
    defaultLocation: "pantry",
    defaultUnit: "g",
    shelfLifeDays: { fridge: 365, freezer: 365, pantry: 540 },
  },
  frozen: {
    label: "Frozen",
    defaultLocation: "freezer",
    defaultUnit: "g",
    shelfLifeDays: { fridge: 2, freezer: 180, pantry: 1 },
  },
  drinks: {
    label: "Drinks",
    defaultLocation: "fridge",
    defaultUnit: "ml",
    shelfLifeDays: { fridge: 14, freezer: 90, pantry: 180 },
  },
  other: {
    label: "Other",
    defaultLocation: "pantry",
    defaultUnit: "pcs",
    shelfLifeDays: { fridge: 7, freezer: 90, pantry: 30 },
  },
};
